//
// WebAppProvider
//
import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useReducer,
  useMemo,
  useRef,
} from 'react';
import superagent from 'superagent';

import {
  config,
  displayJson,
  logError,
  logDev,
  logColor,
} from '../../utils';
import useBrowser from '../browser/useBrowser';

import {
  getPWADisplayMode,
  subscribeUserToPush,
  unsubscribeUserFromPush,
} from './utils';
import WebAppContext from './WebAppContext';

const defaultState = {
  // Object used to ask user for webApp installation, provided by browser
  // when it feels like it!
  installPromptEvent: undefined,
  // Push registration status
  notificationsStatus: 'init',
  // Set when site is installed as a webApp
  webAppMode: getPWADisplayMode(),
  webAppInstalled: ['standalone', 'twa'].includes(getPWADisplayMode()),
  // Set when worker has identified the js cache as being outdated:
  jsBundlesStale: false,
  // worker version, sent back by worker when we send it the dev status
  serviceWorkerRevision: undefined,
  // Set during wervice worker & cache updates:
  serviceWorkerUpdating: false,
  // Wether the user refused to install the webApp in this session:
  rejectedInstall: null,
};

const stateReducer = (state, action) => {
  switch (action.type) {
    case 'setInstallPromptEvent':
      return ({
        ...state,
        installPromptEvent: action.installPromptEvent,
      });

    case 'jsBundlesStale':
      return ({
        ...state,
        jsBundlesStale: action.info,
      });

    case 'setServiceWorkerRevision':
      return ({
        ...state,
        serviceWorkerRevision: action.value,
      });

    case 'notificationsStatus':
      return ({
        ...state,
        notificationsStatus: action.status,
      });

    case 'reset':
      return ({ ...defaultState });

    case 'serviceWorkerUpdating':
      return ({
        ...defaultState,
        serviceWorkerUpdating: true,
      });

    case 'rejectedInstall':
      return ({
        ...state,
        rejectedInstall: action.rejectedInstall,
      });

    default:
      throw new Error(`WebAppProvider unknown action=${displayJson(action, 2)}`);
  }
};

const WebAppProvider = function WebAppProviderModel(props) {
  const {
    workerRegistration,
    children,
  } = props;
  const [state, dispatch] = useReducer(stateReducer, defaultState);
  const { browser } = useBrowser();
  const {
    isIOS,
    isSafari,
    isLegacyIOS,
  } = browser;
  const staleAppCheckIntervalRef = useRef();
  // Guards the reopen JS-hash check against concurrent runs
  const reopenCheckBusyRef = useRef(false);

  //
  // Capture app install prompt the browser opens on its own
  // Keep that prompt info so we can suggest installing the app later on when we want to
  //
  useEffect(
    () => {
      const beforeInstallPromptHandler = (event) => {
        // Prevent the mini-infobar from appearing on mobile
        event.preventDefault();
        dispatch({
          type: 'setInstallPromptEvent',
          installPromptEvent: event,
        });
      };

      window.addEventListener('beforeinstallprompt', beforeInstallPromptHandler);

      return () => {
        window.removeEventListener('beforeinstallprompt', beforeInstallPromptHandler);
      };
    },
    [],
  );

  //
  // Send some config info to the service worker
  //
  useEffect(
    () => {
      if (workerRegistration) {
        if (navigator.serviceWorker.controller) {
          const deviceInfo = {
            type: browser.deviceType,
            os: browser.osName,
            browser: browser.browserName,
            rev: browser.browserVersion,
          };
          if (['standalone', 'twa'].includes(state.webAppMode)) {
            deviceInfo.mode = state.webAppMode;
          }

          navigator.serviceWorker.controller.postMessage({
            type: 'SET_NOTIF_ACK_URL',
            url: null,
            // url: config.anon.api.urlPrefix
            //     + config.anon.api.notif.ack,
          });

          navigator.serviceWorker.controller.postMessage({
            type: 'SET_DEVICE_INFO',
            deviceInfo,
          });
        }
      }
    },
    [workerRegistration, browser, state.webAppMode],
  );

  //
  // If available, use the install prompt we captured earlier
  //
  const suggestInstall = useCallback(
    () => {
      console.log('suggestInstall');
      dispatch({
        type: 'rejectedInstall',
        rejectedInstall: false,
      });

      if (state.installPromptEvent) {
        console.log('suggestInstall 1');
        logDev(`Install, state.installPromptEvent=${displayJson(state.installPromptEvent, 1)}`);

        state.installPromptEvent.prompt();
        state.installPromptEvent.userChoice.then((choiceResult) => {
          if (choiceResult.outcome === 'accepted') {
            logDev('User accepted the install prompt');
          } else {
            logDev('User dismissed the install prompt');
          }
        });
      } else {
        console.log('suggestInstall 2');
        logError(
          `Cannot install, state.installPromptEvent=${state.installPromptEvent}`,
          'WebAppProvider.suggestInstall.noPromptEvent',
          {
            hasPromptEvent: !!state.installPromptEvent,
          },
        );
      }
    },
    [state.installPromptEvent],
  );

  const rejectInstall = useCallback(
    () => {
      dispatch({
        type: 'rejectedInstall',
        rejectedInstall: true,
      });
    },
    [],
  );

  //
  //
  // service-worker for webApp & push notifications setup
  // Get its status.
  //
  useEffect(
    () => {
      const getSWStatus = async () => {
        if (state.notificationsStatus === 'init') {
          if (isIOS && ((!state.webAppInstalled)
                        || (!isSafari)
                        || isLegacyIOS)
          ) {
            //
            // No iOS web push support in any browser,
            // ONLY in installed Safari apps on recent iOS devices
            //
            dispatch({
              type: 'notificationsStatus',
              status: 'notificationsUnavailable',
            });
          } else if (!('PushManager' in window)) {
            //
            // browser doesn't support push notif
            //
            dispatch({
              type: 'notificationsStatus',
              status: 'notificationsUnavailable',
            });
          } else if (workerRegistration) {
            console.log('Service worker got registered before React launch:', workerRegistration);

            dispatch({
              type: 'notificationsStatus',
              status: 'serviceWorkerRegistered',
            });
          } else if ('serviceWorker' in navigator) {
            //
            // Check if service worker has been registered (outside the React logic)
            //
            let registration;
            try {
              registration = await navigator.serviceWorker.getRegistration();
            } catch (err) {
              logError(
                `WebAppProvider getSWStatus getRegistration ${err}`,
                'WebAppProvider.getSWStatus.getRegistration',
                { err: err?.message ?? String(err) },
              );
            }

            if (registration) {
              logError(
                'Service worker is somehow registered:',
                'WebAppProvider.getSWStatus.unexpectedRegistration',
                {
                  scope: registration?.scope,
                  hasActive: !!registration?.active,
                },
              );

              dispatch({
                type: 'notificationsStatus',
                status: 'serviceWorkerRegistered',
              });
            } else {
              console.log('No service worker is registered.');

              dispatch({
                type: 'notificationsStatus',
                status: 'serviceWorkerFailed',
              });
            }
          } else if (!('serviceWorker' in navigator)) {
            dispatch({
              type: 'notificationsStatus',
              status: 'serviceWorkerUnavailable',
            });
          }

          //
          // Poke the worker to get its version in return
          //
          navigator.serviceWorker?.controller?.postMessage({ type: 'POKE_REV' }); // NOSONAR nothing stringified
        }
      };

      // Use void to make it clear we do not want to wait this async function:
      void getSWStatus();
    },
    [
      state.notificationsStatus, workerRegistration, state.webAppInstalled,
      isIOS, isSafari, isLegacyIOS,
    ],
  );

  //
  // updateWebApp: clear the service worker's JS bundles cache, and
  // reload the page
  //
  const updateWebApp = useCallback(
    async () => {
      if (!navigator.serviceWorker?.controller) {
        logError(
          `WebAppProvider updateWebApp, navigator.serviceWorker.controller=${
            navigator.serviceWorker?.controller}`,
          'WebAppProvider.updateWebApp.noController',
          {
            hasServiceWorker: !!navigator?.serviceWorker,
            hasWorkerRegistration: !!workerRegistration,
          },
        );
      }

      let registration = workerRegistration;
      if ((!registration) && (navigator.serviceWorker)) {
        registration = await navigator.serviceWorker.getRegistration();
      }

      if (registration) {
        dispatch({ type: 'serviceWorkerUpdating' });

        // Prepare rest of instructions to run once the new service
        // worker kicks in:
        const onControllerChange = () => {
          console.log('New service worker is controlling the page');
          const newController = navigator.serviceWorker.controller;
          // Now clear the JS cache
          if (newController) {
            logDev('WebAppProvider - Send CLEAR_JS_CACHE (event based)');

            newController.postMessage({ type: 'CLEAR_JS_CACHE' });
          } else {
            console.log(`WebAppProvider navigator.serviceWorker=${
              navigator.serviceWorker}`); // NOSONAR debug log
            console.log(`WebAppProvider navigator.serviceWorker?.controller=${
              navigator.serviceWorker?.controller}`); // NOSONAR debug log
          }
          // Remove the event listener after it's fired
          navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
        };

        logDev('WebAppProvider - Updating service worker...');
        registration.update().then(() => {
          // When worker comes back online, run rest of update:
          navigator.serviceWorker.addEventListener(
            'controllerchange',
            onControllerChange,
          );

          const newController = navigator.serviceWorker.controller;
          if (newController) {
            logDev('WebAppProvider - Send CLEAR_JS_CACHE (right away)');

            newController.postMessage({ type: 'CLEAR_JS_CACHE' });
          } else {
            logDev('newController undefined, waiting...');
          }

          logDev('WebAppProvider - Service worker updated.');
        });
      } else {
        logError(
          `WebAppProvider updateWebApp, registration=${registration}`,
          'WebAppProvider.updateWebApp.noRegistration',
          {
            hasServiceWorker: !!navigator?.serviceWorker,
            hasWorkerRegistration: !!workerRegistration,
          },
        );

        // No service worker, reloading index.html will simply reload all required chunks
        window.location.reload();
      }
    },
    [workerRegistration],
  );

  //
  // On app (re)open, run the update flow as soon as the deployed hash differs from the
  // one this bundle was built with.
  //
  // A stored baseline would not do: browsers evict it, and an update that never lands
  // leaves it claiming a bundle the client is not running.
  //
  // The retry guard holds the hash we tried, so an attempt that does not settle costs one
  // reload per deploy instead of disabling the check.
  //
  const reloadIfJsStale = useCallback(
    async () => {
      if (reopenCheckBusyRef.current) return;

      // Empty on the dev server, and if the file was missing at build time
      if (!__PX_JS_HASH__) return;

      reopenCheckBusyRef.current = true;
      try {
        const liveResponse = await fetch('/jsHash.txt', { cache: 'no-store' });
        if (!liveResponse.ok) return;
        const liveHash = (await liveResponse.text()).trim();
        if (!liveHash) return;

        if (liveHash === __PX_JS_HASH__) {
          // Running the deployed build: let the worker drop a stored hash left behind by a hand reload
          navigator.serviceWorker?.controller?.postMessage({
            type: 'SYNC_JS_HASH',
            hash: liveHash,
          });
          return;
        }

        if (sessionStorage.getItem('pxReloadedForJsHash') === liveHash) return;

        sessionStorage.setItem('pxReloadedForJsHash', liveHash);

        logColor(`WebAppProvider - reopen: JS hash changed (${__PX_JS_HASH__} -> ${liveHash})`, 'orange');
        await updateWebApp();
      } catch (err) {
        logDev(`reloadIfJsStale error: ${err}`);
      } finally {
        reopenCheckBusyRef.current = false;
      }
    },
    [updateWebApp],
  );

  //
  // Run the reopen check on initial mount and whenever the app returns to the
  // foreground: a sleeping/installed PWA resumes without a cold boot, firing
  // visibilitychange (and pageshow on bfcache restore) rather than remounting.
  //
  useEffect(
    () => {
      // Use void to make it clear we do not want to wait this async function:
      void reloadIfJsStale();

      const onVisibilityChange = () => {
        // Use void to make it clear we do not want to wait this async function:
        if (document.visibilityState === 'visible') void reloadIfJsStale();
      };
      const onPageShow = () => reloadIfJsStale();

      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('pageshow', onPageShow);

      return () => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('pageshow', onPageShow);
      };
    },
    [reloadIfJsStale],
  );

  //
  // updateNotifStatus: Check wether we have an active push notif registration or not
  //
  const updateNotifStatus = useCallback(
    async () => {
      if ((state.notificationsStatus === 'serviceWorkerRegistered')
          || (state.notificationsStatus === 'ready')) {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
          // console.warn('Service workers or push messaging not supported in this browser.');
          return;
        }

        let subscribed;
        try {
          const registration = await navigator.serviceWorker.ready;
          const { permission } = Notification;

          if (permission === 'granted') {
            const subscription = await registration.pushManager.getSubscription();
            if (subscription) {
              console.log('User is subscribed to push notifications.', subscription);
              subscribed = true;
            // } else {
            //   console.log(
            // 'Notification permission granted but not subscribed to push notifications.');
            }
          // } else if (permission === 'denied') {
          //   console.log('User has denied notifications.');
          // } else {
          //   console.log('Notification permission has not been requested yet.');
          }
        } catch (error) {
          console.error('updateNotifStatus - Error checking notification and subscription status:', error);
        }

        if (subscribed) {
          dispatch({
            type: 'notificationsStatus',
            status: 'ready',
          });
        } else {
          dispatch({
            type: 'notificationsStatus',
            status: 'serviceWorkerRegistered',
          });
        }
      }
    },
    [state.notificationsStatus],
  );

  //
  // Fetch the JS bundles / source code hash so we know if the cache is stale.
  // If stale, we will get a message from the service-worker
  // Also touch service-worker.
  //
  // Repeat check every 10mn
  //
  useEffect(
    () => {
      const fetchJsHash = async () => {
        if (staleAppCheckIntervalRef.current) clearInterval(staleAppCheckIntervalRef.current);

        let registration = workerRegistration;
        if ((!registration) && (navigator.serviceWorker)) {
          registration = await navigator.serviceWorker.getRegistration();
        }

        if (registration) {
          try {
            await superagent.get('/empty.js');

            await superagent.get('/service-worker.js');
          } catch (err) {
            console.log(`fetchJsHash error: ${err}`);
          }

          staleAppCheckIntervalRef.current = setInterval(
            async () => {
              try {
                await superagent.get('/empty.js');
              } catch (err) {
                console.log(`fetchJsHash error: ${err}`);
              }
            },
            10 * 60 * 1000,
          );
        } else {
          console.log('WebAppProvider - cannot refresh worker cache');
        }
      };

      fetchJsHash().catch((err) => logError(
        `WebAppProvider fetchJsHash ${err}`,
        'WebAppProvider.fetchJsHash',
        { err: err?.message ?? String(err) },
      ));

      // Cleanup interval on unmount
      return () => {
        if (staleAppCheckIntervalRef.current) {
          clearInterval(staleAppCheckIntervalRef.current);
        }
      };
    },
    [workerRegistration],
  );

  //
  // Get messages from service worker:
  //  - stale JS cache notifications
  //  - worker version
  //
  useEffect(
    () => {
      if (navigator) {
        if (navigator.serviceWorker) {
          if (navigator.serviceWorker.addEventListener) {
            navigator.serviceWorker.addEventListener(
              'message',
              (event) => {
                switch (event?.data?.type) {
                  case 'JS_BUNDLES_STALE': {
                    logColor(`WebAppProvider - JS bundles stale, info=${displayJson(event.data?.info)}`, 'orange');

                    dispatch({
                      type: 'jsBundlesStale',
                      info: event.data?.info,
                    });
                    if (event.data?.version) {
                      dispatch({
                        type: 'setServiceWorkerRevision',
                        value: event.data?.version,
                      });
                    }
                    break;
                  }

                  case 'WORKER_VERSION': {
                    logDev(`WebAppProvider - Service worker version ${event.data?.version} (${
                      event.data?.context})`);

                    if (event.data?.version) {
                      dispatch({
                        type: 'setServiceWorkerRevision',
                        value: event.data?.version,
                      });
                    }
                    break;
                  }

                  case 'RELOAD_PAGE': {
                    logColor('Reload page', 'purple');
                    // App update post service worker & caches update
                    window.location.reload();

                    break;
                  }

                  case 'NOTIF':
                  case 'CLICK_NOTIF':
                  //
                  // Will be handled in NotificationsProvider
                    break;

                  default:
                    logError(
                      `WebAppProvider unknown message data=${displayJson(event?.data)}`,
                      'WebAppProvider.onMessage.unknownType',
                      {
                        messageType: event?.data?.type,
                        keys: event?.data && Object.keys(event.data),
                      },
                    );
                }
              },
            );
          } else {
            logDev('WebAppProvider navigator.serviceWorker.addEventListener undefined');
          }
        } else {
          logDev(`WebAppProvider navigator.serviceWorker=${navigator.serviceWorker}`);
        }
      } else {
        logError(
          'WebAppProvider navigator undefined?',
          'WebAppProvider.initServiceWorker.noNavigator',
          { hasNavigator: typeof navigator !== 'undefined' },
        );
      }
    },
    [],
  );

  const notificationsRegister = useCallback(
    async () => {
      // For now, do not support
      if (state.notificationsStatus) {
        throw (new Error('No notifis for now'));
      }

      if (state.notificationsStatus === 'serviceWorkerRegistered') {
        //
        // Get the VAPID from the server
        //
        let url = config.anon.api.urlPrefix
                + config.anon.api.notif.vapid;

        let vapid;
        try {
          const res = await superagent
            .get(url)
            .set(['Content-Type', 'application/json'])
            .withCredentials();

          [vapid] = res.body.rslt.data;
          dispatch({
            type: 'notificationsStatus',
            status: 'vapidOk',
          });
        } catch (err) {
          logError(
            `notificationsRegister vapid error: ${err}`, // NOSONAR error stringified for logging
            'WebAppProvider.notificationsRegister.vapid',
            {
              err: err?.message ?? String(err), // NOSONAR error stringified for logging
              ...(err?.status && { status: err.status }),
            },
          );
          dispatch({
            type: 'notificationsStatus',
            status: 'vapidFailed',
          });
          return null;
        }

        const subscription = await subscribeUserToPush(vapid);

        if (!subscription) {
          dispatch({
            type: 'notificationsStatus',
            status: 'subscriptionFailed',
          });
          return null;
        }

        url = config.anon.api.urlPrefix
            + config.anon.api.notif.register;

        try {
          console.log(`Send body=${displayJson(subscription)} to url=${url}`);
          await superagent
            .post(url)
            .responseType('text')
            .set(['Content-Type', 'application/json'])
            .withCredentials()
            .send(subscription.toJSON());

          dispatch({
            type: 'notificationsStatus',
            status: 'ready',
          });
          return true;
        } catch (err) {
          logError(
            `notificationsRegister error: ${err}`, // NOSONAR error stringified for logging
            'WebAppProvider.notificationsRegister.error',
            {
              err: err?.message ?? String(err), // NOSONAR error stringified for logging
              ...(err?.status && { status: err.status }),
            },
          );
          dispatch({
            type: 'notificationsStatus',
            status: 'registerFailed',
          });
          return null;
        }
      }

      return null;
    },
    [state.notificationsStatus],
  );

  const notificationsUnregister = useCallback(
    async () => {
      if (state.notificationsStatus === 'ready') {
        await unsubscribeUserFromPush();

        const url = config.anon.api.urlPrefix
                  + config.anon.api.notif.unsubscribe;

        try {
          console.log(`Send to url=${url}`);
          await superagent
            .post(url)
            .responseType('text')
            .set(['Content-Type', 'application/json'])
            .withCredentials()
            .send({});
        } catch {
          // console.log(`notificationsRegister error: ${err}`);
        }

        dispatch({
          type: 'notificationsStatus',
          status: 'serviceWorkerRegistered',
        });
      }

      return null;
    },
    [state.notificationsStatus],
  );

  const contextValue = useMemo(
    () => ({
      webApp: {
        notifications: {
          register: notificationsRegister,
          status: state.notificationsStatus,
          unsubscribe: notificationsUnregister,
          update: updateNotifStatus,
        },
        suggestInstall,
        rejectInstall,
        rejectedInstall: state.rejectedInstall,
        installable: !!state.installPromptEvent,
        installed: state.webAppInstalled,
        mode: state.webAppMode,
        updateAvailable: state.jsBundlesStale,
        update: updateWebApp,
        updating: state.serviceWorkerUpdating,
        devInfo: {
          workerAvailable: !!navigator.serviceWorker,
          workerRev: state.serviceWorkerRevision,
        },
        isIos: state.isIOS,
        isSafari: state.isSafari,
        isLegacyIOS: state.isLegacyIOS,
      },
    }),
    [
      notificationsRegister, state.notificationsStatus, suggestInstall, state.webAppInstalled,
      notificationsUnregister, updateWebApp, updateNotifStatus, state.serviceWorkerRevision,
      state.installPromptEvent, state.jsBundlesStale, state.serviceWorkerUpdating,
      state.isIOS, state.isSafari, state.isLegacyIOS, state.webAppMode, rejectInstall,
      state.rejectedInstall,
    ],
  );

  return (
    <WebAppContext.Provider
      value={contextValue}
    >
      {children}
    </WebAppContext.Provider>
  );
};

WebAppProvider.propTypes = {

  workerRegistration: PropTypes.object,
  children: PropTypes.node.isRequired,
};

export default WebAppProvider;
